export function formatBackendDateTime(
  value?: string | null,
  emptyValue = '--',
): string {
  return formatBackendDateTimeByLocale(value, getCurrentAppLocale(), emptyValue);
}

export function formatBackendDateTimeByLocale(
  value?: string | null,
  locale?: string | null,
  emptyValue = '--',
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) {
    return emptyValue;
  }

  const parsed = parseBackendDateTime(value);
  if (!parsed) {
    return emptyValue;
  }

  const normalizedLocale = normalizeLocaleTag(locale);
  const resolvedOptions = options ?? {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  try {
    if (!options) {
      const dateFormatter = new Intl.DateTimeFormat(normalizedLocale, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });
      const timeFormatter = new Intl.DateTimeFormat(normalizedLocale, {
        hour: '2-digit',
        minute: '2-digit',
      });
      const separator = (normalizedLocale ?? '').toLowerCase().startsWith('ar') ? '، ' : ', ';
      return `${dateFormatter.format(parsed)}${separator}${timeFormatter.format(parsed)}`;
    }
    return parsed.toLocaleString(normalizedLocale, resolvedOptions);
  } catch {
    if (!options) {
      const dateFormatter = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });
      const timeFormatter = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${dateFormatter.format(parsed)}, ${timeFormatter.format(parsed)}`;
    }
    return parsed.toLocaleString(undefined, resolvedOptions);
  }
}

export function normalizeLocaleTag(locale?: string | null): string | undefined {
  if (!locale) {
    return undefined;
  }

  const normalized = locale.trim().replace('_', '-');
  if (!normalized) {
    return undefined;
  }

  return normalized;
}

function getCurrentAppLocale(): string | undefined {
  try {
    const stored = localStorage.getItem('appLang');
    return normalizeLocaleTag(stored);
  } catch {
    return undefined;
  }
}

export function parseBackendDateTime(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const raw = value.trim();
  if (!raw) {
    return null;
  }

  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (dateOnlyPattern.test(raw)) {
    const parsedDateOnly = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsedDateOnly.getTime()) ? null : parsedDateOnly;
  }

  const looksLikeIsoDateTime = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(raw);
  const hasTimezoneInfo = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);
  let normalized = raw;

  if (looksLikeIsoDateTime) {
    normalized = raw.replace(' ', 'T');
    // Values without an explicit timezone come from datetime-local inputs and
    // should be displayed as the same wall-clock time the instructor entered.
    if (hasTimezoneInfo) {
      normalized = raw.replace(' ', 'T');
    }
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

export function formatDateYyyyMmDd(
  value?: string | null,
  emptyValue = '-',
): string {
  if (!value) {
    return emptyValue;
  }

  const raw = value.trim();
  const isoDatePrefixMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDatePrefixMatch) {
    return isoDatePrefixMatch[1];
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return emptyValue;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateYyyyMmDdWithTime(
  value?: string | null,
  emptyValue = '-',
): string {
  if (!value) {
    return emptyValue;
  }

  const raw = value.trim();
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (dateOnlyPattern.test(raw)) {
    return raw;
  }

  const parsed = parseBackendDateTime(raw);
  if (!parsed) {
    return emptyValue;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}, ${hour}:${minute}`;
}
